import React, { useEffect, useRef, useState } from "react";
import "./OwButton.scss";
import {isNil}from "lodash-es";
import { FormattedMessage } from "react-intl";
import ISupplierModel from "../../../models/ISupplierModel";
import OwLabel from "../OwLabel/OwLabel";

type Props = {
    disabled?: boolean;
    active?: boolean;
    disableOnActive?: boolean;
    /**
     * Required function for the standard click button
     *
     * @param e
     */
    fn?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    className?: string;
    leftIconClass?: string;
    rightIconClass?: string;
    centerIconClass?: string;
    iconTitle?: string;
    title?: string;
    ariaLabel?: string;
    minWidth?: number;
    isSupplierVendorOverride?: boolean;
    supplierModel?: ISupplierModel;
    activeTitle?: string;
    hoverTitle?: string;
    tabIndex?: number;
    postTitle?: string;
    id?: string;
    btnSizeClass?: string;
    type?: "button" | "submit" | "reset" | undefined;
    form?: string;
    skipTranslate?: boolean;
    testId?: string;
    /**
     * Required function for the press and hold button
     */
    onHold?: () => void;
    /**
     * (Optional) Delay in milliseconds before the hold action triggers
     */
    holdDelay?: number;
    /**
     * Children elements to be rendered inside the button
     */
    children?: React.ReactNode;
    disableDblClickPrevention?: boolean;
    labelClassName?: string;
    name?: string;
};

/**
 * OwButton
 *
 * This is a drop-in replacement of the standard HTML Button, but allows you to
 * control certain aspects of the button with flags in the parent component.
 *
 * The button offers to basic varieties:
 * 1) A standard click button
 * 2) A press and hold button
 *
 * The standard click button requires just the fn function, where as the press and hold button requires using the
 * onHold function.
 *
 * @property title string                       The title for the button
 * @property disabled boolean                   Disables the Button Control
 * @property active boolean                     Updates the Button Control with a Spinner Indicator
 * @property disableOnActive boolean            Automatically disable the Button when Active State
 * @property fn function                        onClick function
 * @property className string                   Classes applied to the button
 * @property leftIconClass string               An icon to show on the Left Side of the Button Control (ie: fas fa-box)
 * @property rightIconClass string              An icon to show on the Right Side of the Button Control (ie: fas fa-box)
 * @property centerIconClass string             An icon to show in the Center of the Button Control (ie: fas fa-box)
 * @property iconTitle string                   A title to display under the icon (small font  applied)
 * @property minWidth number                    The minimum width to set the Button Control to
 * @property isSupplierVendorOverride boolean   If the title has a value that should be swapped for a different supplier term
 * @property supplierModel ISupplierModel       The Supplier Model
 * @property activeTitle string                 The title to show on the Button Control when Active State
 * @property hoverTitle string                  The title to show on the Button Control when Hover State
 * @property postTitle string                   A string that will not be translated, and after the title
 * @property testId string                      A string of the test id for the button
 */
const OwButton = (props: Props) => {
    const owButton = useRef<HTMLButtonElement>(null);
    const [width, setWidth] = useState<number | undefined>(undefined);
    const [isHolding, setIsHolding] = useState(false);
    const [isClickLocked, setIsClickLocked] = useState(false); // Prevent double clicks
    const holdTimer = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        return () => {
            if (holdTimer.current) {
                clearTimeout(holdTimer.current);
            }
        };
    }, []);

    const isHoldMode = !!props.onHold;

    const startHold = () => {
        if (!props.disabled && !props.active && isHoldMode) {
            setIsHolding(true);
            const delay = props.holdDelay || 500;
            holdTimer.current = setTimeout(() => {
                if (props.onHold) {
                    props.onHold(); // Execute onHold directly
                    setIsHolding(false); // Reset holding state after execution
                }
            }, delay);
        }
    };

    const stopHold = () => {
        if (holdTimer.current) {
            clearTimeout(holdTimer.current);
            holdTimer.current = null;
        }
        setIsHolding(false);
    };

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (!isHoldMode && !isHolding && !isClickLocked) {
            if (!props.disableDblClickPrevention) {
                setIsClickLocked(true); // Lock further clicks
            }

            const currentButtonWidth = e.currentTarget.getBoundingClientRect().width;
            e.currentTarget.setAttribute("style", `width: ${currentButtonWidth}px`);

            if (props.fn) {
                props.fn(e);
            }

            if (!props.disableDblClickPrevention) {
                // Unlock after a delay (e.g., 1 second)
                setTimeout(() => {
                    setIsClickLocked(false);
                }, 1000);
            }
        }
    };

    const handleMouseDown = () => {
        if (isHoldMode) {
            startHold();
        }
    };

    const handleMouseUp = () => {
        if (isHoldMode) {
            stopHold();
        }
    };

    const handleMouseLeave = () => {
        if (isHoldMode) {
            stopHold();
        }
    };

    const handleTouchStart = (e: React.TouchEvent<HTMLButtonElement>) => {
        if (isHoldMode) {
            e.preventDefault();
            startHold();
        }
    };

    const handleTouchEnd = () => {
        if (isHoldMode) {
            stopHold();
        }
    };

    let buttonClassName = "btn-primary";
    if (props.className) {
        buttonClassName = props.className;
    }

    let buttonTitle;
    if (!isNil(props.title) && props.title.length) {
        let messageTitle = props.title;
        if (props.isSupplierVendorOverride && props.supplierModel && !isNil(props.supplierModel.vendor_name_override)) {
            messageTitle = messageTitle.replace(/vendor/, props.supplierModel.vendor_name_override);
        }
        buttonTitle = props.skipTranslate ? (
            messageTitle
        ) : (
            <FormattedMessage id={`${messageTitle}`} defaultMessage={`_${messageTitle}_`} description="OW Button Title" />
        );
    } else if (!isNil(props.centerIconClass) && props.centerIconClass.length) {
        buttonTitle = <i data-testid={"btn-center-icon"} className={props.centerIconClass} />;
    }

    const leftIcon = props.leftIconClass ? <i className={`me-2 ${props.leftIconClass}`} title={props.iconTitle} /> : null;

    const rightIcon = props.rightIconClass ? <i className={`ms-2 ${props.rightIconClass}`} title={props.iconTitle} /> : null;

    const buttonPostTitle = props.postTitle || null;

    let innerHtml = (
        <>
            {leftIcon}
            {buttonTitle}
            {buttonPostTitle}
            {rightIcon}
        </>
    );

    if (props.labelClassName) {
        <span className={props.labelClassName}>{innerHtml}</span>
    }

    let buttonWidth = width;
    let isDisabled = props.disabled;

    if (props.active) {
        const activeTitleElement = props.activeTitle ? <OwLabel className="mb-0 ms-2" title={props.activeTitle} /> : null;
        innerHtml = (
            <span>
                <i className="fas fa-circle-notch fa-spin" title={props.iconTitle} />
                {activeTitleElement}
            </span>
        );
        buttonWidth = owButton?.current?.getBoundingClientRect()?.width || 100;
        if (props.disableOnActive) {
            isDisabled = true;
        }
    }

    const holdAnimationDuration = `${(props.holdDelay || 500) / 1000}s`; // Convert ms to s

    return (
        <button
            role={"button"}
            name={props.name}
            data-testid={props.testId}
            form={props.form}
            type={props.type || "button"}
            id={props.id}
            tabIndex={props.tabIndex}
            title={props.hoverTitle}
            style={
                {
                    width: buttonWidth,
                    "--hold-duration": holdAnimationDuration, // Pass holdDelay to CSS
                } as React.CSSProperties
            }
            ref={owButton}
            className={`${props.btnSizeClass || "btn-sm"} btn owButton ${buttonClassName} ${isHolding && isHoldMode ? "holding" : ""}`}
            disabled={isDisabled}
            onClick={handleClick}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}>
            {innerHtml}
            {props.children}
        </button>
    );
};

export default OwButton;
